import {
    AiImagePresetStyles,
    AiMessage,
    GenerationFile,
    OutlineQuestion,
    OutlineQuestionAnswer,
    PresentationPromptType
} from "common/aiConstants";
import { IPresentation, IPresentationStorageModel, ISlide, IStorageModel } from "common/interfaces";

import { AmplitudeCategories, trackEvent } from "./amplitude";
import { getBrandKitTrackingProps, getCurrentBrandKitTrackingProps } from "./brandKitTracking";

const AUDIT_OMITTED_PROPS = [
    "prompt",
    "messages",
    "custom_style_type_prompt",
    "files",
    "answers",
    "question_ids",
    "unanswered_question_ids",
    "error_message"
];

/** `audit: true` writes the event to the Postgres `analytics` table the release readout is built from. */
export const AUDITED = { audit: true, auditOmit: AUDIT_OMITTED_PROPS };

/** Mirrors GenerateStep on the client, kept as its own list so a rename there is a deliberate decision here. */
export const GenerationStep = {
    PROMPT: "prompt",
    INTERIM: "interim",
    OUTLINE: "outline"
} as const;

/** Where the flow was entered from. The landing-page variant skips the interim step. */
export const GenerationEntryPoint = {
    CREATE_DIALOG: "create_dialog",
    CREATE_EMBEDDED: "create_embedded",
    LANDING_PAGE: "landing_page",
    MOBILE_CREATE: "mobile_create"
} as const;

/** The stage a failure happened at, so errors can be attributed to a step rather than a dialog. */
export const GenerationErrorStage = {
    QUESTIONS: "questions",
    OUTLINE: "outline",
    REFINE: "refine",
    CHAT: "chat",
    REGENERATE: "regenerate",
    DECK: "deck",
    FILE_PARSE: "file_parse",
    EMPTY_EDIT: "empty_edit"
} as const;

/** What the user changed on the outline, for the "do they actually edit it" question. */
export const OutlineEditType = {
    TITLE: "title",
    BODY: "body",
    VISUAL_IDEA: "visual_idea",
    SOURCES: "sources",
    ADD_SLIDE: "add_slide",
    GENERATE_SLIDE: "generate_slide",
    DELETE_SLIDE: "delete_slide",
    REORDER: "reorder"
} as const;

/** The in-app Back button and the browser's are different signals, so they are reported separately. */
export const BackTrigger = {
    BUTTON: "button",
    BROWSER: "browser"
} as const;

/** Which surface a generation setting was changed from. */
export const SettingSurface = {
    PROMPT_BAR: "prompt_bar",
    BRAND_KIT_DIALOG: "brand_kit_dialog",
    INTERIM_SECTION: "interim_section",
    OUTLINE_FOOTER: "outline_footer",
    OUTLINE_CHAT: "outline_chat"
} as const;

/** The state of one generation attempt. Every flow event carries all of it, so any field can be a breakdown axis. */
export interface GenerationFlowState {
    generationId: string,
    entryPoint?: string,
    step?: string,
    promptType?: PresentationPromptType | null,
    refineRequested?: boolean,
    skipClarifyingQuestions?: boolean,
    prompt?: string | null,
    files?: GenerationFile[],
    theme?: any,
    brandKitChanged?: boolean,
    imageSource?: string,
    imageStyle?: string,
    language?: string,
    preserveExactText?: boolean,
    webSearch?: boolean,
    citeSources?: string | null,
    outlineQuestions?: OutlineQuestion[],
    questionAnswers?: OutlineQuestionAnswer[],
    outline?: { slides?: unknown[] } | null,
    // Set only for an attempt that began from a link: `linkMode` is the destination it asked for,
    // `promptSource` is whoever put the link out.
    linkMode?: string | null,
    promptSource?: string | null
}

/** Read off the model: built-in themes carry a flag, and shared and personal kits persist to different roots. */
function _getBrandKitSource(theme: any) {
    if (!theme) {
        return null;
    }
    if (theme.get?.("isBuiltIn")) {
        return "builtin";
    }
    return theme.root === "sharedThemes" ? "shared" : "personal";
}

function _getImageStyleProps(imageStyle?: string) {
    if (!imageStyle) {
        return { image_style: null, custom_style_type: null };
    }

    const isPreset = AiImagePresetStyles.some(style => style.style === imageStyle);
    return {
        image_style: isPreset ? imageStyle : "custom",
        custom_style_type: isPreset ? "preset" : "prompt"
    };
}

/** The shared property bag for the guided flow, joined on `generation_id` (the controller's instance id). */
export function getGenerationFlowProps(flow: GenerationFlowState) {
    const questions = flow.outlineQuestions ?? [];
    const answers = flow.questionAnswers ?? [];

    return {
        generation_id: flow.generationId,
        entry_point: flow.entryPoint ?? null,
        step: flow.step ?? null,
        // "unclassified" rather than null, so the prompt step does not collapse into "no value" in a breakdown.
        path: flow.promptType ?? "unclassified",
        refined: Boolean(flow.refineRequested),
        skipped_questions_step: Boolean(flow.skipClarifyingQuestions),
        // The length, not the text: this bag rides on every audited event, so the prompt is never written
        // to the analytics table. Same reason the answers below collapse to FREE_TEXT_ANSWER.
        prompt_length: flow.prompt?.length ?? 0,
        ..._getFilesProps(flow.files ?? [], { usedOnly: false }),
        brand_kit_id: flow.theme?.id ?? null,
        brand_kit_name: flow.theme?.get?.("name") ?? null,
        brand_kit_source: _getBrandKitSource(flow.theme),
        brand_kit_changed: Boolean(flow.brandKitChanged),
        image_source: flow.imageSource ?? null,
        ..._getImageStyleProps(flow.imageStyle),
        language: flow.language ?? null,
        preserve_exact_text: Boolean(flow.preserveExactText),
        web_search: Boolean(flow.webSearch),
        cite_sources: flow.citeSources ?? null,
        questions_offered: questions.length,
        questions_answered: answers.length,
        outline_slide_count: flow.outline?.slides?.length ?? null,
        // Null for an attempt someone typed themselves, which separates organic use from link traffic.
        link_mode: flow.linkMode ?? null,
        prompt_source: flow.promptSource ?? null
    };
}

export function GeneratePresentation({ presentation: _presentation, flow, theme }: {
    presentation: IPresentation | IPresentationStorageModel,
    // Passed explicitly because the controller resets — and so rotates its generation id — while finishing.
    flow?: GenerationFlowState,
    theme?: any
}) {
    const presentation: IPresentation = ("get" in _presentation) ? _presentation.attributes : _presentation;

    const metadata = presentation.metadata ?? {};

    const isPresetImageStyle = AiImagePresetStyles.some(style => style.style === metadata.imageStyle);

    // Passed in rather than read off the presentation, where a shared kit is only named by id.
    const brandKitProps = getBrandKitTrackingProps(theme, {
        isShared: theme ? null : (presentation.sharedThemeId ? true : null)
    });

    const props = {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        ...brandKitProps,
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: presentation.id,
        // Lands in the audit table's own presentation_id column, which is what joins a deck to its generation.
        presentationId: presentation.id,
        num_slides: Object.keys(presentation.slideRefs).length,
        from_tour: false,
        used_ai_images: metadata.imageSource === "ai",
        image_source: metadata.imageSource ?? null,
        fallback_image_source: _getFallbackImageSource(metadata.imageSource, metadata.fallbackImageSource),
        model_name: metadata.imageModel,
        image_quality: metadata.imageQuality,
        custom_style_type: isPresetImageStyle ? "preset" : "prompt",
        custom_style_type_prompt: isPresetImageStyle ? null : metadata.imageStyle,
        image_style: isPresetImageStyle ? metadata.imageStyle : "custom",
        language: metadata.language,
        prompt: metadata.prompt,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch
    };

    trackEvent(AmplitudeCategories.Presentation, "Generate", props, AUDITED);
}

/**
 * Only Team Assets can miss, so the fallback is only meaningful there - every other source stores one
 * it never consults, and reporting that would read as though it had been used.
 */
function _getFallbackImageSource(imageSource?: string | null, fallbackImageSource?: string | null) {
    return imageSource === "team" ? fallbackImageSource ?? null : null;
}

/** `usedOnly` splits "files this generation consumed" from "files attached right now". */
function _getFilesProps(files: GenerationFile[], { usedOnly = true } = {}) {
    const countedFiles = usedOnly ? files.filter(file => file.hasBeenUsed) : files;
    return {
        files: countedFiles.map(({ rawFileName, parsedFileName, hasBeenUsedFor }) => ({ rawFileName, parsedFileName, hasBeenUsedFor })),
        files_count: countedFiles.length
    };
}

export function GeneratePresentationOutline({ prompt, files, webSearch, flow }: { prompt: string, files: GenerationFile[], webSearch: boolean, flow?: GenerationFlowState }) {
    const props = {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Generate", props, AUDITED);
}

export function RegeneratePresentationOutline({ prompt, files, webSearch, flow }: { prompt: string, files: GenerationFile[], webSearch: boolean, flow?: GenerationFlowState }) {
    const props = {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Regenerate", props, AUDITED);
}

export function ChatEditPresentationOutline({ prompt, messages, files, webSearch, flow, chatShortcut }: {
    prompt: string,
    messages: AiMessage[],
    files: GenerationFile[],
    webSearch: boolean,
    flow?: GenerationFlowState,
    // Which of the canned "Ask me to..." shortcuts was used, if any.
    chatShortcut?: string | null
}) {
    const props = {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        ..._getFilesProps(files),
        prompt: prompt,
        messages: messages.map(({ role, content }) => ({ role, content })),
        web_search: webSearch,
        chat_shortcut: chatShortcut ?? null
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "ChatEdit", props, AUDITED);
}

// Stands in for an answer the user typed, so the event keeps its shape without carrying their words.
const FREE_TEXT_ANSWER = "__free_text__";

/** `prompt_type` is the fork in the flow, and splits every downstream funnel number by path. */
export function GenerateOutlineQuestions({ prompt, questions, promptType, forced, flow }: {
    prompt: string,
    questions: OutlineQuestion[],
    promptType?: PresentationPromptType | null,
    forced?: boolean,
    flow?: GenerationFlowState
}) {
    trackEvent(AmplitudeCategories.PresentationOutline, "GenerateQuestions", {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        prompt,
        prompt_type: promptType ?? "unclassified",
        // True when the questions were asked because the user opted into refining a pasted outline.
        forced: Boolean(forced),
        questions_count: questions.length,
        question_ids: questions.map(question => question.id),
        question_priorities: questions.map(question => question.priority)
    }, AUDITED);
}

/** Paired with GenerateOutlineQuestions on the same path, this is the refine opt-in rate. */
export function RefineOutlineRequested({ flow }: { flow?: GenerationFlowState }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "RefineRequested", {
        ...(flow ? getGenerationFlowProps(flow) : {})
    }, AUDITED);
}

/** Leaving the interim step backwards. Only a deliberate exit is observable, so this undercounts abandonment. */
export function AbandonInterimStep({ promptType, questions, answers, flow }: {
    promptType?: PresentationPromptType | null,
    questions: OutlineQuestion[],
    answers?: OutlineQuestionAnswer[],
    flow?: GenerationFlowState
}) {
    trackEvent(AmplitudeCategories.PresentationOutline, "AbandonInterimStep", {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        prompt_type: promptType ?? "unclassified",
        questions_count: questions.length,
        answered_count: (answers ?? []).length
    }, AUDITED);
}

/** Per-question skip and answer rates: the signal the questions prompt gets tuned against. */
export function AnswerOutlineQuestions({ questions, answers, skipped, flow }: { questions: OutlineQuestion[], answers?: OutlineQuestionAnswer[], skipped: boolean, flow?: GenerationFlowState }) {
    const answeredIds = new Set((answers ?? []).map(answer => answer.id));

    // Offered options report as themselves and anything typed reports only its presence. Note that
    // "offered" does not mean safe: the model writes these options per request, out of the user's own
    // prompt. That is why `answers` is not written to the audit table — see AUDIT_OMITTED_PROPS.
    const offeredValuesById = new Map(questions.map(question =>
        [question.id, new Set((question.options ?? []).map(option => option.value))]));

    trackEvent(AmplitudeCategories.PresentationOutline, "AnswerQuestions", {
        ...(flow ? getGenerationFlowProps(flow) : {}),
        skipped,
        questions_count: questions.length,
        answered_count: answeredIds.size,
        question_ids: questions.map(question => question.id),
        unanswered_question_ids: questions.filter(question => !answeredIds.has(question.id)).map(question => question.id),
        answers: (answers ?? []).map(({ id, values }) => ({
            id,
            values: values.map(value => offeredValuesById.get(id)?.has(value) ? value : FREE_TEXT_ANSWER)
        }))
    }, AUDITED);
}

/** The denominator for everything below: users who saw the prompt screen, before anyone typed. */
export function StartGenerationFlow({ flow }: { flow: GenerationFlowState }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "StartFlow", getGenerationFlowProps(flow), AUDITED);
}

/** Where an attempt begins. Separate from StartFlow so "opened it" vs "used it" is measurable. */
export function SubmitGenerationPrompt({ flow, usedRecentPrompt, usedSpeechToText, fromDeepLink }: {
    flow: GenerationFlowState,
    usedRecentPrompt?: boolean,
    usedSpeechToText?: boolean,
    // A marketing deep link that prefilled and auto-submitted the prompt.
    fromDeepLink?: boolean
}) {
    trackEvent(AmplitudeCategories.PresentationOutline, "SubmitPrompt", {
        ...getGenerationFlowProps(flow),
        used_recent_prompt: Boolean(usedRecentPrompt),
        used_speech_to_text: Boolean(usedSpeechToText),
        from_deep_link: Boolean(fromDeepLink)
    }, AUDITED);
}

/** The funnel's spine: one event per step reached. The drop-off report groups on `step`. */
export function ViewGenerationStep({ flow, interimVariant }: { flow: GenerationFlowState, interimVariant?: string | null }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "ViewStep", {
        ...getGenerationFlowProps(flow),
        // Which shape the interim step took: the pasted-outline offer, or the questions.
        interim_variant: interimVariant ?? null
    }, AUDITED);
}

/** A default was overridden rather than accepted. One parameterized event rather than one per control. */
export function ChangeGenerationSetting({ flow, setting, value, surface, fromKitDefault }: {
    flow: GenerationFlowState,
    setting: string,
    value: unknown,
    surface?: string,
    // True when the value being set is the one the selected brand kit supplies, i.e. adopted rather than overridden.
    fromKitDefault?: boolean
}) {
    trackEvent(AmplitudeCategories.PresentationOutline, "ChangeSetting", {
        ...getGenerationFlowProps(flow),
        setting,
        value,
        surface: surface ?? null,
        from_kit_default: Boolean(fromKitDefault)
    }, AUDITED);
}

/** The generated outline was edited by hand. Callers throttle to once per edit type per attempt. */
export function EditOutline({ flow, editType }: { flow: GenerationFlowState, editType: string }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "EditOutline", {
        ...getGenerationFlowProps(flow),
        edit_type: editType
    }, AUDITED);
}

/** Paired with ViewStep, this separates deliberate retreat from abandonment. */
export function NavigateBackInFlow({ flow, fromStep, toStep, discardedAnswers, discardedOutline, trigger }: {
    flow: GenerationFlowState,
    fromStep: string,
    toStep: string,
    discardedAnswers?: boolean,
    discardedOutline?: boolean,
    trigger?: string
}) {
    trackEvent(AmplitudeCategories.PresentationOutline, "NavigateBack", {
        ...getGenerationFlowProps(flow),
        from_step: fromStep,
        to_step: toStep,
        discarded_answers: Boolean(discardedAnswers),
        discarded_outline: Boolean(discardedOutline),
        trigger: trigger ?? null
    }, AUDITED);
}

/** The flow was left without producing a deck. Fired on unmount, so a closed tab is not counted. */
export function ExitGenerationFlow({ flow, reason }: { flow: GenerationFlowState, reason?: string }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "ExitFlow", {
        ...getGenerationFlowProps(flow),
        reason: reason ?? "unmount",
        reached_outline: Boolean(flow.outline)
    }, AUDITED);
}

/** Attributed to the stage the failure happened at rather than the dialog that showed it. */
export function GenerationError({ flow, stage, error }: { flow: GenerationFlowState, stage: string, error?: any }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "Error", {
        ...getGenerationFlowProps(flow),
        stage,
        // Status separates "we refused" (entitlement, rate limit) from "it broke".
        error_status: error?.status ?? null,
        error_name: error?.name ?? null,
        error_message: typeof error?.message === "string" ? error.message.slice(0, 500) : null
    }, AUDITED);
}

function _getSlide(_slide: ISlide | IStorageModel<ISlide>) {
    const slide: ISlide = ("get" in _slide) ? _slide.attributes : _slide;
    return slide;
}

function _getSlideProps(_slide: ISlide | IStorageModel<ISlide>) {
    const slide = _getSlide(_slide);

    const metadata = slide.metadata ?? {};

    const imageSource = metadata.imageSource ?? metadata.generatedModel?.slideImage?.imageSource;
    const imageStyle = metadata.imageStyle ?? metadata.generatedModel?.slideImage?.imageStyle;
    const imageModel = metadata.imageModel ?? metadata.generatedModel?.slideImage?.imageModel;
    const imageQuality = metadata.imageQuality ?? metadata.generatedModel?.slideImage?.imageQuality;
    const isPresetImageStyle = imageStyle ? AiImagePresetStyles.some(style => style.style === imageStyle) : null;

    return {
        // Slides are always generated against the theme loaded for the open
        // presentation, so the editor's current kit is the one that applied.
        ...getCurrentBrandKitTrackingProps(),
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: slide.presentationId,
        slide_id: slide.id,
        prompt: metadata.slidePrompt,
        language: metadata.language,
        used_ai_images: imageSource === "ai",
        image_source: imageSource ?? null,
        fallback_image_source: _getFallbackImageSource(imageSource, metadata.fallbackImageSource),
        model_name: imageModel,
        image_quality: imageQuality,
        custom_style_type: imageStyle ? (isPresetImageStyle ? "preset" : "prompt") : null,
        custom_style_type_prompt: imageStyle ? (isPresetImageStyle ? null : imageStyle) : null,
        image_style: imageStyle ? (isPresetImageStyle ? imageStyle : "custom") : null,
        context_source: metadata.files?.length ? "file" : undefined,
        use_presentation_as_context: metadata.presentationAsContext,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch,
        slide_type: metadata.slideType,
        template_id: slide.template_id
    } as Record<string, any>;
}

export function GenerateSlide({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    trackEvent(AmplitudeCategories.Slide, "Generate", props);
}

export function GenerateSlideIdeas({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.generated_ideas = _getSlide(_slide).metadata?.generatedIdeas?.map(({ slideCategory, slideConfidence, slideReasoning }) => ({ category: slideCategory, confidence: slideConfidence, reasoning: slideReasoning }));
    trackEvent(AmplitudeCategories.Slide, "GenerateIdeas", props);
}

export function SelectSlideIdea({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.selected_template_id = props.template_id;
    trackEvent(AmplitudeCategories.Slide, "SelectIdea", props);
}

export function RegenerateImagePrompt({ slide: _slide, slidePrompt, presentationTopic, generatedImagePrompt }: { slide: ISlide | IStorageModel<ISlide>, slidePrompt: string, presentationTopic: string, generatedImagePrompt: string }) {
    const props = _getSlideProps(_slide);
    props.slide_prompt = slidePrompt;
    props.presentation_topic = presentationTopic;
    props.generated_image_prompt = generatedImagePrompt;
    trackEvent(AmplitudeCategories.Image, "RegeneratePrompt", props);
}
